/* eslint-disable no-console */
import axios, {
  AxiosHeaders,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios';

import { auth } from '@/fire';
import { UnknownObject } from '@/models/misc';
import getRoute from '@/utils/getRoute';

let isRefreshingToken = false;

// Queue to store failed requests
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const apiURL = process.env.REACT_APP_BACKEND_API_URL;

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const getHttpConfig = (
  config: AxiosRequestConfig
): AxiosRequestConfig => ({
  ...config,
  headers: {
    ...config.headers,
    'Content-Type': 'application/json'
  }
});

export const api = axios.create({ baseURL: apiURL });

export const decomposeParams = (params?: UnknownObject) =>
  params
    ? `?${Object.entries(params)
        .filter(([, value]) => ![undefined, null].includes(value))
        .map(([key, value]) => `${key}=${value}`)
        .join('&')}`
    : '';

// Interceptor for adding the token to each request
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { currentUser } = auth;

    const newConfig = { ...config };

    const newHeadersInstance = new AxiosHeaders(config.headers);
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken(); // Get the actual token
        if (token) {
          newHeadersInstance.set('Authorization', `Bearer ${token}`);
        }
      } catch (error) {
        console.error('Error getting ID token in request interceptor:', error);
      }
    }
    if (!newHeadersInstance.has('Content-Type')) {
      newHeadersInstance.set('Content-Type', 'application/json');
    }

    newConfig.headers = newHeadersInstance;
    return newConfig;
  },
  (error) => Promise.reject(error)
);

// Interceptor for handling responses, especially 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If it's a 401 error and not a retry attempt
    if (error.response?.status === 401 && !originalRequest.retry) {
      if (isRefreshingToken) {
        // If the token is being refreshed, add the request to the queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest); // Retry with the new token
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest.retry = true; // mark the request as a retry to avoid infinite loops
      isRefreshingToken = true;

      const { currentUser } = auth;
      if (!currentUser) {
        // If there's no Firebase user, there's no way to refresh.
        isRefreshingToken = false;
        processQueue(new Error('No Firebase user for token refresh'), null);
        window.location.href = getRoute.auth.LOGOUT();
        return Promise.reject(
          new Error('User not authenticated for token refresh.')
        );
      }

      try {
        // Force a refresh token with Firebase
        const newToken = await currentUser.getIdToken(true);
        isRefreshingToken = false;
        processQueue(null, newToken); // Process the queue with the new token

        // Update the original request header and retry
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshingToken = false;
        processQueue(refreshError as Error, null); // Process the queue with an error
        console.error('Unable to refresh token:', refreshError);

        window.location.href = getRoute.auth.LOGOUT();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
